import React, { useCallback, useContext, useEffect, useState } from 'react';

import AuthContext from '../AuthContext';
import { LanguageContext } from '../LanguageContext';
import api from '../services/api';
import styles from './NotificationSettingsPanel.module.css';

const NotificationSettingsPanel = () => {
  const { authToken } = useContext(AuthContext);
  const { t } = useContext(LanguageContext);
  const [settings, setSettings] = useState(null);
  const [deviceSettings, setDeviceSettings] = useState([]);
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const loadData = useCallback(async () => {
    if (!authToken) {
      return;
    }
    setIsLoading(true);
    setLoadError('');
    const [settingsRes, deviceRes, historyRes] = await Promise.allSettled([
      api.get('/notifications/settings/'),
      api.get('/notifications/device-settings/'),
      api.get('/notifications/history/'),
    ]);
    if (settingsRes.status === 'fulfilled') {
      setSettings(settingsRes.value.data);
    } else {
      setLoadError(t('notificationSettingsLoadError'));
    }
    if (deviceRes.status === 'fulfilled') {
      setDeviceSettings(deviceRes.value.data || []);
    } else {
      setDeviceSettings([]);
    }
    if (historyRes.status === 'fulfilled') {
      setHistory(historyRes.value.data || []);
    } else {
      setHistory([]);
    }
    setIsLoading(false);
  }, [authToken]);

  useEffect(() => {
    loadData().catch((error) => {
      console.error('Notification panel load error:', error);
      setLoadError(t('notificationSettingsLoadError'));
      setIsLoading(false);
    });
  }, [loadData, t]);

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      const response = await api.patch('/notifications/settings/', settings);
      setSettings(response.data);
      await loadData();
    } catch (error) {
      console.error('Save notification settings error:', error);
      alert(t('saveError'));
    }
  };

  const updateDeviceToggle = async (deviceId, field, value) => {
    try {
      const response = await api.patch(`/notifications/device-settings/${deviceId}/`, { [field]: value });
      setDeviceSettings((prev) => prev.map((row) => (row.device === deviceId ? response.data : row)));
    } catch (error) {
      console.error('Update device notification settings error:', error);
      alert(t('saveError'));
    }
  };

  if (isLoading) {
    return (
      <section className={styles.panel}>
        <p className={styles.muted}>{t('loading')}</p>
      </section>
    );
  }

  if (!settings) {
    return (
      <section className={styles.panel}>
        <p className={styles.muted}>{loadError || t('notificationSettingsLoadError')}</p>
      </section>
    );
  }

  return (
    <section className={styles.panel}>
      {loadError && <p className={styles.muted}>{loadError}</p>}
      <h2>{t('notificationSettings')}</h2>
      <form onSubmit={saveSettings}>
        <div className={styles.formRow}>
          <label className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>{t('contactEmail')}</span>
            <input
              type="email"
              value={settings.contact_email || ''}
              onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
              placeholder={t('contactEmail')}
            />
          </label>
          <label className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>{t('preferredLanguage')}</span>
            <select
              value={settings.preferred_language || 'pl'}
              onChange={(e) => setSettings({ ...settings, preferred_language: e.target.value })}
            >
              <option value="pl">Polski</option>
              <option value="en">English</option>
              <option value="de">Deutsch</option>
            </select>
          </label>
          <label className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>{t('offlineThresholdMinutes')}</span>
            <input
              type="number"
              min="1"
              value={settings.offline_threshold_minutes ?? 360}
              onChange={(e) => setSettings({ ...settings, offline_threshold_minutes: Number(e.target.value || 0) })}
              placeholder={t('offlineThresholdMinutes')}
            />
          </label>
          <label className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>{t('warningThresholdPercent')}</span>
            <input
              type="number"
              step="0.1"
              min="0"
              value={settings.warning_threshold_percent ?? 70}
              onChange={(e) => setSettings({ ...settings, warning_threshold_percent: Number(e.target.value || 0) })}
              placeholder={t('warningThresholdPercent')}
            />
          </label>
        </div>
        <div className={styles.actions}>
          <button className={styles.button} type="submit">{t('save')}</button>
        </div>
      </form>

      <h3>{t('notificationsPerFilter')}</h3>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t('devices')}</th>
              <th>MAC</th>
              <th>{t('notifyOffline')}</th>
              <th>{t('notifyReconnected')}</th>
              <th>{t('notifyFilterWarning')}</th>
              <th>{t('notifyFilterReplacement')}</th>
              <th>{t('notifyFilterReplacementReminder')}</th>
            </tr>
          </thead>
          <tbody>
            {deviceSettings.map((row) => (
              <tr key={row.device}>
                <td>{row.device_name}</td>
                <td>{row.mac}</td>
                <td><input type="checkbox" checked={!!row.notify_offline} onChange={(e) => updateDeviceToggle(row.device, 'notify_offline', e.target.checked)} /></td>
                <td><input type="checkbox" checked={!!row.notify_reconnected} onChange={(e) => updateDeviceToggle(row.device, 'notify_reconnected', e.target.checked)} /></td>
                <td><input type="checkbox" checked={!!row.notify_filter_warning} onChange={(e) => updateDeviceToggle(row.device, 'notify_filter_warning', e.target.checked)} /></td>
                <td><input type="checkbox" checked={!!row.notify_filter_replacement} onChange={(e) => updateDeviceToggle(row.device, 'notify_filter_replacement', e.target.checked)} /></td>
                <td><input type="checkbox" checked={!!row.notify_filter_replacement_reminder} onChange={(e) => updateDeviceToggle(row.device, 'notify_filter_replacement_reminder', e.target.checked)} /></td>
              </tr>
            ))}
            {deviceSettings.length === 0 && (
              <tr>
                <td colSpan={7} className={styles.muted}>{t('noResults')}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <h3 style={{ marginTop: 16 }}>{t('notificationHistory')}</h3>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t('last')}</th>
              <th>{t('devices')}</th>
              <th>{t('notificationType')}</th>
              <th>{t('status')}</th>
              <th>{t('recipient')}</th>
              <th>{t('notificationDetails')}</th>
            </tr>
          </thead>
          <tbody>
            {history.map((row) => (
              <tr key={row.id}>
                <td>{new Date(row.created_at).toLocaleString()}</td>
                <td>{row.device_name || row.mac || '-'}</td>
                <td>{t(`notificationType_${row.notification_type}`)}</td>
                <td>{t(`notificationStatus_${row.status}`)}</td>
                <td>{row.recipient_email || '-'}</td>
                <td>{row.error_message || '-'}</td>
              </tr>
            ))}
            {history.length === 0 && (
              <tr>
                <td colSpan={6} className={styles.muted}>{t('noResults')}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default NotificationSettingsPanel;
